import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { borderLine } from "../../../utils/pdp/common";
import Widget from "../widget";
import { getProductsByIdsData, getProductsFromByIdsResponse } from "../../../api/plpApi";
import { PageTypes } from "../../../utils/fnpPageType";
import Typography from "../../atoms/typography";
import { campaignEvents } from "../../../analytics/clevertapConstants";
import useAnalytics from "../../../analytics/useAnalytics";

/**
 * CleverTapWidget dynamically renders product recommendation widgets based on user behavior,
 * triggered by a custom CleverTap event.
 *
 * @component
 * @param {Object} props - Props passed to the component
 * @param {string} props.fromPage - Indicates the source page type (e.g., PRODUCT, HOME)
 * @param {boolean} props.isMobile - Determines if the view is mobile or desktop
 * @param {string} [props.skuCode] - SKU code of the current product (if applicable)
 * @returns {JSX.Element|null} - Rendered widget component(s) or null
 */
const CleverTapWidget = ({ fromPage, isMobile, skuCode, headingClass, slidesToShow }) => {
  const [cTapWidget, setCTapWidget] = useState([]);
  const [cTapWidgetComponents, setCTapWidgetComponents] = useState([]);
  const { trackEvent } = useAnalytics();

  /**
   * Maps page types to their corresponding CleverTap topic identifiers.
   * Extend this map for more page types as needed.
   */
  const topicMap = {
    [PageTypes.PRODUCT]: "Recommendation_widget_pdp",
    [PageTypes.HOME]: "Recommendation_widget_home",
    [PageTypes.CATEGORY]: "Recommendation_widget_plp",
    [PageTypes.MY_ACCOUNT]: "Recommendation_widget_myaccount",
  };

  useEffect(() => {
    /**
     * Registers an event listener for `CT_web_native_display` and stores the relevant widget data.
     */
    const handleEvent = (event) => {
      const { kv } = event?.detail || {};
      if (!kv) return;
      const { topic, widget_name, pid } = kv || {};
      if (!topic || !widget_name || !pid) return;
      const expectedTopicPart = topicMap[fromPage];
      if (expectedTopicPart && !topic.includes(expectedTopicPart)) {
        return;
      }
      trackEvent(campaignEvents.RENDER_NOTIFICATION_VIEWED, event?.detail, ["clevertap"]);
      setCTapWidget((prevWidgets) => {
        const isAlreadyPresent = prevWidgets.some((widget) => widget.topic === topic);
        if (isAlreadyPresent) return prevWidgets;
        return [
          ...prevWidgets,
          {
            topic,
            widget_name,
            pid: pid.replace(/\s/g, ""),
            campaign_details: event?.detail,
          },
        ];
      });
    };

    document.addEventListener("CT_web_native_display", handleEvent);
    return () => document.removeEventListener("CT_web_native_display", handleEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pageTypeClasses = {
    [PageTypes.HOME]: "relative w-full md:px-40",
    [PageTypes.CATEGORY]: "my-8 w-full",
  };

  useEffect(() => {
    if (cTapWidget.length === 0) {
      setCTapWidgetComponents([]);
      return;
    }

    /**
     * Fetches product data for each widget based on PID and sets corresponding components.
     */
    const fetchWidgets = async () => {
      // eslint-disable-next-line no-undef
      const widgetElements = await Promise.all(
        cTapWidget.map(async ({ topic, widget_name, pid, campaign_details }) => {
          try {
            const pids = pid?.split(",") || [];
            const response = await getProductsByIdsData(pids);

            const products = getProductsFromByIdsResponse(response);
            if (products.length === 0) return null;
            products.forEach((item) => {
              item.campaign_details = campaign_details;
            });
            return (
              <div key={widget_name} className={pageTypeClasses[fromPage] || ""}>
                {fromPage === PageTypes.HOME && (
                  <Typography
                    variant="h1"
                    Tag="p"
                    classes={`font-600 text-16 mb-12 md:mb-20 px-16 md:px-0 lg:text-24 xxl:text-32 ${headingClass || ""} capitalize`}
                  >
                    {widget_name}
                  </Typography>
                )}
                <Widget
                  widgetTitle={widget_name}
                  productsList={products}
                  isMobile={isMobile}
                  type={topic}
                  position={1}
                  skuCode={skuCode || ""}
                  pageType={fromPage}
                  titleStyle="pl-0"
                  isShowTitle={fromPage === PageTypes.HOME ? false : true}
                  slidesToShow={slidesToShow ? 5 : 4}
                />
                {fromPage !== PageTypes.HOME && borderLine()}
              </div>
            );
          } catch (error) {
            console.error("Error fetching widget data:", error);
            return null;
          }
        }),
      );

      setCTapWidgetComponents(widgetElements.filter(Boolean)); // remove nulls
    };

    fetchWidgets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cTapWidget]);

  return <>{cTapWidgetComponents}</>;
};

CleverTapWidget.propTypes = {
  fromPage: PropTypes.string.isRequired,
  isMobile: PropTypes.bool.isRequired,
  skuCode: PropTypes.string,
  headingClass: PropTypes.string,
  // eslint-disable-next-line react/boolean-prop-naming
  slidesToShow: PropTypes.bool,
};

export default CleverTapWidget;
